import { z } from 'zod';

import { parsePage, type PageResult } from '@/lib/query/page';

const clientId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

export const clientWriteSchema = z.object({
  id: clientId,
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(1).max(32),
  email: z.string().max(191).default(''),
  licenseNumber: z.string().max(64).default(''),
  cinPassport: z.string().max(64).default(''),
  address: z.string().max(2000).default(''),
  riskLevel: z.enum(['clean', 'watch', 'blacklisted']).default('clean'),
  riskNote: z.string().max(2000).default(''),
  rentalsCount: z.number().int().min(0).max(100000).default(0),
  totalSpent: z.number().min(0).max(100000000).default(0),
});

export const clientStoreSchema = clientWriteSchema.omit({ id: true }).extend({
  id: clientId.optional(),
});

export const clientRecordSchema = clientWriteSchema.extend({
  createdAt: z.string().default(''),
});

export const clientReplaceSchema = z
  .object({
    clients: z.array(clientWriteSchema).max(400),
  })
  .superRefine((body, context) => {
    const seen = new Set<string>();
    for (const client of body.clients) {
      if (seen.has(client.id)) {
        context.addIssue({
          code: 'custom',
          path: ['clients'],
          message: 'Two clients use the same id.',
        });
        return;
      }
      seen.add(client.id);
    }
  });

export const clientListSchema = z.array(clientRecordSchema);

export function parseClientPage(payload: unknown): PageResult<z.infer<typeof clientRecordSchema>> {
  return parsePage(payload, clientRecordSchema, 'The client list');
}

export function clientWriteError(error: z.ZodError): string {
  return error.issues[0]?.message ?? 'Invalid input';
}
